/**
 * The tap-to-skip ramp of the story video player (albums, video messages, Portals, the event
 * gallery), plus the step vocabulary the voice-note dock's fan offers.
 *
 * The VOICE DOCK no longer ramps (Oct 13 2026): every tap there is the chosen step and nothing
 * is announced, because the ramp's feedback pill read as a cap on how fast you could tap.
 * Its step comes from `voiceSkipOptions` / `voiceSkipStep`; the chain below is the video's.
 *
 * A tap less than SKIP_FAST_MS after the previous one in the same direction climbs a tier;
 * a slower tap (up to SKIP_RESET_MS) repeats the tier; no tap for SKIP_RESET_MS, or a change
 * of direction, resets to the first tier. The chain also carries the burst's running total
 * so the feedback pill can read `+18S`.
 */
export const SKIP_FAST_MS = 450;
export const SKIP_RESET_MS = 1000;

/** The video tiers (seconds per tap) for a clip of this length. */
export function videoSkipTiers(durationSec) {
  const d = Number.isFinite(durationSec) ? durationSec : 0;
  if (d >= 600) return [3, 5, 10, 25];
  if (d >= 120) return [3, 5, 10];
  return [3, 5];
}

/** The steps a voice note of this length offers in the skip fan. */
export function voiceSkipOptions(durationSec) {
  return (Number.isFinite(durationSec) && durationSec > 300) ? [3, 5, 10, 15, 30] : [3, 5, 10];
}

/**
 * The step a voice note actually uses: the stored preference when this note offers it,
 * else the largest step it does offer (30 s on a 0:48 note becomes 10).
 */
export function voiceSkipStep(durationSec, preferred) {
  const opts = voiceSkipOptions(durationSec);
  if (opts.includes(preferred)) return preferred;
  return opts[opts.length - 1];
}

/**
 * Advance the chain for a tap. `chain` is the previous value (or null); the result is the
 * next chain, with `step` set to the seconds this tap should seek by. Pure — the caller
 * keeps the chain in a ref and adds the distance actually travelled via `addTravel`.
 *
 *   { dir: -1|1, tier: index into tiers, total: seconds this burst, at: ms of this tap, step }
 */
export function nextSkip(chain, now, tiers, dir) {
  const list = tiers && tiers.length ? tiers : [3];
  let next;
  if (!chain || chain.dir !== dir || now - chain.at > SKIP_RESET_MS) {
    next = { dir, tier: 0, total: 0, at: now };
  } else {
    const climb = now - chain.at < SKIP_FAST_MS;
    next = { dir, tier: climb ? Math.min(chain.tier + 1, list.length - 1) : chain.tier, total: chain.total, at: now };
  }
  next.step = list[next.tier];
  next.max = list[list.length - 1];
  return next;
}

/** The chain with the seconds a seek really moved folded into its total (clamped seeks move less). */
export function addTravel(chain, seconds) {
  if (!chain) return chain;
  return { ...chain, total: chain.total + Math.round(Math.abs(seconds)) };
}

/** `+18S` / `−8S` — the burst's running total as the pill prints it. */
export function burstLabel(chain) {
  if (!chain) return '';
  return `${chain.dir < 0 ? '−' : '+'}${chain.total}S`;
}

/** `10S PER TAP` with ` · MAX` on the top tier (the video pill's second line). */
export function perTapLabel(chain) {
  if (!chain) return '';
  return `${chain.step}S PER TAP${chain.step === chain.max ? ' · MAX' : ''}`;
}

/** `3S · 5S · 10S` — the tiers as the coach overlay lists them. */
export function tiersLine(tiers) {
  return (tiers || []).map((t) => `${t}S`).join(' · ');
}
